import { useQueryClient } from '@tanstack/react-query';
import { useSupportAccounts } from '@my/directory-api/hooks';
import { setSupportSession, useSupportSession } from '@my/platform/support';
import { SearchableSelect } from '@my/ui';
import { LifeBuoy, Plus } from 'lucide-react';
import { useNavigate } from 'react-router';

/**
 * The Stratum support strip: which customer My Hub is pointed at, and the way
 * to start a new one.
 *
 * <p>
 * THIS IS THE STAFF-ONLY PIECE. The sidebar's own account switcher above is the
 * ordinary one — a user moving between accounts they belong to — and it stays
 * when this portal opens up to account owners. This strip does not: reaching
 * into an account you are not a member of, and creating one for somebody who is
 * not yet a customer, are both things only Stratum does.
 * </p>
 *
 * <p>
 * RED, exactly as the support bar in the operational apps is, and by the same
 * rule: red means you are acting as Stratum support with powers no customer
 * has. One signal, learned once, wherever it appears.
 * </p>
 *
 * <p>
 * The obvious objection — that a permanently red strip becomes wallpaper —
 * does not hold here: warning fatigue comes from a warning that fires when
 * nothing special is happening, and for a support identity something special is
 * always happening. It also matters more later, not less: once account owners
 * use this portal, staff and owners see near-identical screens, which is
 * precisely the condition the colour exists for.
 * </p>
 *
 * <p>
 * Grey until a customer is chosen, as the apps' bar is — the colour tracks
 * whether you are actually inside somebody's account, not merely whether you
 * could be.
 * </p>
 */
export function AccountContextBar() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isError, isLoading } = useSupportAccounts();
  const { session } = useSupportSession();

  // Not support: the strip is not theirs, and an empty one would only raise a
  // question they cannot act on.
  if (isError) return null;

  const accounts = data ?? [];
  const active = Boolean(session?.accountId);

  /**
   * Options, plus the current session if the list has not caught up with it.
   *
   * <p>
   * A just-created account is selected before the list refetches, and an option
   * list that does not contain the selected value makes the control claim
   * nothing is selected — while every other part of the screen shows the
   * customer. The session knows its own name; use it until the list agrees.
   * </p>
   */
  const options = accounts.map((a) => ({ value: a.id, label: a.name, hint: a.domain }));
  if (session?.accountId && !accounts.some((a) => a.id === session.accountId)) {
    options.unshift({
      value: session.accountId,
      label: session.accountName || 'Selected account',
      hint: '',
    });
  }

  const select = (accountId: string) => {
    const account = accounts.find((a) => a.id === accountId);
    if (!account) return;

    // Session FIRST, so the refetches below go out carrying the new
    // X-Account-Key rather than reloading the customer we are leaving.
    setSupportSession({
      accountId: account.id,
      accountName: account.name,
      // My Hub works at account level. The organisation only matters when
      // handing over to an app, and that picker lives with the handover.
      organisationId: '',
      organisationName: '',
    });

    /**
     * Throw away every cached row and refetch what is on screen.
     *
     * NOT OPTIONAL. Account-scoped queries are keyed without the account —
     * `['directory','users','list']` is the same tuple whichever customer you
     * are in — so the cache will happily serve the previous account's users for
     * the new one. Navigating does not help: the data is cached, so nothing
     * refetches.
     *
     * `resetQueries`, not `clear`: clear empties the cache but leaves the
     * components watching it orphaned on their last render.
     *
     * The support-account list is spared — it is platform-wide, not
     * account-scoped, and resetting it would blank the picker you just used.
     */
    void queryClient.resetQueries({
      predicate: (q) => !(q.queryKey[0] === 'directory' && q.queryKey[1] === 'support'),
    });

    navigate('/account');
  };

  return (
    <div
      role="region"
      aria-label="Stratum support"
      className={`flex w-full flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2 text-white ${
        active ? 'bg-rose-700' : 'bg-slate-700'
      }`}
    >
      <LifeBuoy className="h-4 w-4 shrink-0" aria-hidden />
      <span className="text-sm font-semibold">
        {active ? 'Support session' : 'Support — no account selected'}
      </span>

      <SearchableSelect
        label="Customer account"
        placeholder={isLoading ? 'Loading…' : 'Select an account…'}
        searchPlaceholder="Search accounts…"
        disabled={isLoading}
        // The strip sits at the bottom of the shell, so a menu hanging below it
        // would open off-screen.
        dropUp
        options={options}
        value={session?.accountId ?? ''}
        onChange={select}
      />

      {/* Creating an account for somebody who is not a customer yet is a
          Stratum action, which is why it lives on the Stratum strip rather
          than among the sections an owner will one day use. */}
      <button
        type="button"
        onClick={() => navigate('/accounts/new')}
        className="ml-auto inline-flex items-center gap-1 rounded border border-white/30 px-2 py-1 text-sm hover:bg-white/10"
      >
        <Plus className="h-3.5 w-3.5" aria-hidden />
        Create new account
      </button>
    </div>
  );
}
